import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { Product } from '../models/product.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { RequestModel } from '../typings/typing';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { CartData } from '../models/cartData.model';
import { Address } from '../models/address.model';
import { FormGroup } from '@angular/forms';
import { CommonService } from './common.service';
import { LoginService } from './login.service';
import { CustomerService } from './customer.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  prefix = '/eshop_backend/shoppingCart'
  private cartItemsSubject = new BehaviorSubject<CartData>({
    list: [],
    shippingAddress: null,
    totalAmount: 0,
    netValue: 0,
    netPrice: 0,
    movSurcharge: 0,
    tax: 0,
    shipmentDimension: null,
    errorMessage: [],
    messages: [],
    shippingAddresses: [],
    simulateQuery:"",
    bonusDiscount:0,
    orderTypeList:[],
    totalFreight:0
  });
  cartData$: Observable<CartData> = this.cartItemsSubject.asObservable();
  sortType: string = 'create_time';
  ascType: string = 'asc';
  orderType: string = 'Standard_Sales';
  userStatus: number | null = null;
  customer:any;
  isFirstLoading:boolean=true;
  private autoSelectNonBoschPartKeys = new Map<string, number>();

  constructor(
    private http: HttpClient,
    private message: NzMessageService,
    private translate: TranslateService,
    private commonService: CommonService,
    private loginService: LoginService,
    private customerService:CustomerService
  ) {
    this.loginService.userData$.subscribe((userData: any) => {
      this.userStatus = userData?.queryUserPageInfoResDTO?.status;
    });

    // Subscribe to selectedCustomer changes
    this.customerService.selectedCustomer$.subscribe((customer) => {
      this.customer = customer;
      this.loadCartIfCustomerSelected();
    });

    // Subscribe to language changes
    this.translate.onLangChange.subscribe(() => {
      if(!this.isFirstLoading){
        this.loadCartIfCustomerSelected();
      }

    });
  }

  ngOnInit(): void {
  }
  loadCartIfCustomerSelected() {
    if (this.userStatus === 2 && this.customer?.customerCode) {
      this.getListFromCart(this.sortType, this.ascType);
    }
  }

  setOrderType(value: any) {
    this.orderType = value;
  }

  ensureSevenDaysBuffer(dateStr: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const inputDate = new Date(dateStr);
    inputDate.setHours(0, 0, 0, 0);

    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 7);

    if (this.isAutoPlusSevenNeed() && inputDate < minDate) {
      return minDate;
    }

    return inputDate;
  }

  isAutoPlusSevenNeed() {
    const storedCustomer = localStorage.getItem("customer");
    const customerInfo = storedCustomer ? JSON.parse(storedCustomer) : null;
    const salesOrg = this.getOrgCode();
    if (customerInfo !== null && salesOrg === 'CN20-G4-40') {
      return true;
    } else {
      return false;
    }
  }

  getOrgCode(): string {

    const orgs = ["/iam", "/oes"];
    const router = orgs.find(org => window.location.pathname.includes(org));
    const orgMapping = JSON.parse(localStorage.getItem('allOrgRoutes') || '{}');
    const userOrg = JSON.parse(localStorage.getItem('sales_org') || '[]');
    const orgList = orgMapping[`${router}`];
    if (window.location.href.includes("Salesportal/iam/cart/gettoken")) {
      return 'CN20-G1-40';
    }
    if (!orgList || orgList.length === 0) {
      return '';
    }
    if (orgList.includes('CN20-G1-40') && userOrg.includes('CN20-G1-40')) {
      return 'CN20-G1-40';
    }

    const firstMatchingOrg = orgList.find((org: any) => userOrg.includes(org));

    return firstMatchingOrg ? firstMatchingOrg : orgList[0];
  }

  async addToCart(products: Product[]): Promise<void> {
    //Send a request to add to shopping cart
    const productsWithSysUserId = products.map((product) => {
      if (product.expectedDate) {
        const adjusted = this.ensureSevenDaysBuffer(product.expectedDate);
        return { ...product, expectedDate: adjusted };
      }
      return { ...product };
    });
    const headers = new HttpHeaders().set('globalLoading', 'true');
    const addSuccessPromise = new Promise<void>((resolve, reject) => {
      this.http
        .post<RequestModel>(this.prefix + '/insertList', productsWithSysUserId, { headers })
        .subscribe(
          (items: RequestModel) => {
            if (items.rtnCode != 10000) {
              reject();
              return;
            }
            let { totalFreight,list, shippingAddress, orderTypeList,totalAmount,bonusDiscount, simulateQuery,netValue, netPrice, tax, shipmentDimension, errorMessage, shippingAddresses, movSurcharge, messages } = items.rtnData;
            const cartData: CartData = {
              list: list,
              shippingAddress: shippingAddress,
              totalAmount: totalAmount,
              netValue: netValue,
              netPrice: netPrice,
              movSurcharge: movSurcharge,
              messages: messages,
              tax: tax,
              shipmentDimension: shipmentDimension,
              errorMessage: errorMessage,
              shippingAddresses: shippingAddresses,
              simulateQuery: simulateQuery,
              bonusDiscount:bonusDiscount,
              orderTypeList:orderTypeList,
              totalFreight:totalFreight,
              selectedOrderType:orderTypeList.length > 0 ? orderTypeList[0] : undefined
            };
            this.setAutoSelectNonBoschPartKeys(productsWithSysUserId);
            this.cartItemsSubject.next(cartData);
            resolve(); //Call resolve on success
          },
          (error) => {
            reject(error); //Call reject on failure
          }
        );
    });
    await addSuccessPromise; //Waiting for the addToCart operation to complete
  }
  consumeAutoSelectNonBoschPartKeys(): Map<string, number> {
    const keys = new Map(this.autoSelectNonBoschPartKeys);
    this.autoSelectNonBoschPartKeys.clear();
    return keys;
  }

  private setAutoSelectNonBoschPartKeys(products: any[]): void {
    this.autoSelectNonBoschPartKeys.clear();
    products
      .filter((product) => product.isNonBoschPartNo === 1)
      .forEach((product) => {
        const key = this.buildNonBoschPartKey(product);
        this.autoSelectNonBoschPartKeys.set(key, (this.autoSelectNonBoschPartKeys.get(key) || 0) + 1);
      });
  }

  private buildNonBoschPartKey(product: any): string {
    const materialNumber = (product.inputMaterialNumber || product.materialNumber || '')
      .toString()
      .replace(/\s+/g, '')
      .trim()
      .toUpperCase();
    const quantity = Number(product.inputQuantity || product.quantity || 1);
    return `${materialNumber}|${quantity}|${this.formatDateKey(product.expectedDate)}`;
  }

  private formatDateKey(value: any): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value.toString().slice(0, 10);
    }
    return this.commonService.formatDateString(date);
  }

  async markNonBoschPart(cartItemIds: number[]): Promise<void> {
    const headers = new HttpHeaders().set('globalLoading', 'true');
    const markPromise = new Promise<void>((resolve, reject) => {
      this.http
        .post<RequestModel>(this.prefix + '/markNonBoschPart', { cartItemIds }, { headers })
        .subscribe(
          (result: RequestModel) => {
            if (result.rtnCode != 10000) {
              reject(result);
              return;
            }
            this.message.create('success', this.translate.instant('OPERATION_SUCCESS'));
            this.getListFromCart(this.sortType, this.ascType);
            resolve();
          },
          (error) => {
            reject(error);
          }
        );
    });
    await markPromise;
  }
  //forAPi:call api or not
  updateCartItem(product: Product, forApi: boolean = true): void {
    if (!forApi) {
      this.updateCartItemData(product);
      return;
    }
    let params = {
      expectedDate: product.expectedDate,
      id: product.id,
      quantity: product.quantity,
      minimumOrderQuantity: product.minimumOrderQuantity
    };
    sessionStorage.setItem('isUpdatingProduct','true');
    this.http.post(this.prefix + `/update`, params).subscribe(
      (result: any) => {
        let newProduct = result.rtnData;
        sessionStorage.setItem('isUpdatingProduct','false');
        // rush order do not check stock
        // if (this.orderType === 'Rush_Sales') {
        //   const index = newProduct.errorMessages.findIndex((item: any) => item.code === "028");
        //   if (newProduct.stockQuantity < newProduct.quantity || newProduct.stockQuantity === 0) {
        //     if (index == -1) {
        //       newProduct.errorMessages.push({
        //         "code": "028",
        //         "message": this.translate.instant('STOCK_ERROR'),
        //         "severity": "E"
        //       });
        //     }
        //   }
        // }
        this.updateCartItemData(newProduct)
      },
      (error) => {
        console.error('Error updating:', error);
      }
    );
  }
  //update  cartItemsSubject data
  updateCartItemData(product: Product) {
    const currentItems = this.cartItemsSubject.value.list;
    const updatedItems = currentItems.map((item) => {
      if (item.id === product.id) {
        return { ...item, ...product }; //Do not pass product, but pass the returned value in order to obtain the latest error result
      } else {
        return item;
      }
    });

    const cartData: CartData = {
      ...this.cartItemsSubject.value,
      list: updatedItems,
    };

    this.cartItemsSubject.next(cartData);
  }
  removeFromCart( form: FormGroup,productId?: number,selectedIds?:number[]): void {
    let params = {
      id: productId,
      addressId: form.value.addressId,
      ids:selectedIds,
      completeDelivery: form.value.completeDelivery,
      deliveryDate: this.commonService.formatDateString(form.value.deliveryDate),
      dispatchType: form.value.dispatchType,
      orderType: form.value.orderType,
      poNumber: form.value.poNumber,
      brand:form.value.brand
    };
    this.http.post(this.prefix + `/delete`, params).subscribe(
      () => {
        this.message.create(
          'success',
          this.translate.instant('OPERATION_SUCCESS')
        );
        this.getListFromCart(this.sortType, this.ascType);

        // const currentItems = this.cartItemsSubject.value.list;
        // const updatedItems = currentItems.filter(
        //   (item) => item.id !== productId
        // );

        // const cartData: CartData = {
        //   ...this.cartItemsSubject.value,
        //   list: updatedItems,
        // };

        // this.cartItemsSubject.next(cartData);
      },
      (error) => {
        console.error('Error removing:', error);
      }
    );
  }
  removeAllFromCart(isAbnormal: boolean, form?: FormGroup):  Promise<void> {
    return new Promise((resolve, reject) => {
    let ids: any[] = [];
    if (this.cartItemsSubject.value.list) {
      ids = this.cartItemsSubject.value.list
        .filter((item: any) => {
          return item.errorMessages && item.errorMessages.some((msg: any) => msg.severity === 'E');
        })
        .map(item => item.id);
    }
    this.http
      .post(this.prefix + `/deleteAll`, {
        isAbnormal: isAbnormal,
        ids: isAbnormal ? ids : undefined,
        addressId: isAbnormal ? form?.value.addressId : undefined,
        completeDelivery: isAbnormal ? form?.value.completeDelivery : undefined,
        deliveryDate: isAbnormal ? this.commonService.formatDateString(form?.value.deliveryDate) : undefined,
        dispatchType: isAbnormal ? form?.value.dispatchType : undefined,
        orderType: isAbnormal ? form?.value.orderType : undefined,
        poNumber: isAbnormal ? form?.value.poNumber : undefined,
        brand:isAbnormal ? form?.value.brand:undefined
      })
      .subscribe(
        () => {
          this.message.create(
            'success',
            this.translate.instant('OPERATION_SUCCESS')
          );
          this.getListFromCart(this.sortType, this.ascType);
          resolve();
        },
        (error) => {
          console.error('Error removing item from cart:', error);
          reject(error);
        }
      );
  })
}

  getListFromCart(sortType: string, ascType: string): void {
    this.sortType = sortType;
    this.ascType = ascType;
    let params = new HttpParams();
    //Set the sortType parameter (if present)
    if (sortType) {
      params = params.set('column', sortType);
    }
    //Set the ascType parameter (if present)
    params = params.set('order', ascType ?? "");
    const headers = new HttpHeaders().set('globalLoading', 'false');
    //Obtain shopping cart data through an interface
    this.http.get<RequestModel>(this.prefix + '/list', { params , headers}).subscribe(
      (items: RequestModel) => {
        if (items.rtnData !== false) {
          this.isFirstLoading = false;
          let { totalFreight,list, shippingAddress,orderTypeList, poNumber,totalAmount,bonusDiscount,simulateQuery, netValue, netPrice, tax, shipmentDimension, errorMessage, shippingAddresses, movSurcharge, messages } = items.rtnData;
          const cartData: CartData = {
            list: list,
            shippingAddress: shippingAddress,
            totalAmount: totalAmount,
            netValue: netValue,
            netPrice: netPrice,
            bonusDiscount:bonusDiscount,
            tax: tax,
            movSurcharge: movSurcharge,
            messages: messages,
            shipmentDimension: shipmentDimension,
            errorMessage: errorMessage,
            shippingAddresses: shippingAddresses,
            simulateQuery:simulateQuery,
            orderTypeList:orderTypeList,
            totalFreight:totalFreight,
            poNumber: poNumber,
            selectedOrderType:orderTypeList.length > 0 ? orderTypeList[0] : undefined
          };
          this.cartItemsSubject.next(cartData);
        }
      },
      (error) => {
        //Handling errors
      }
    );
  }

  checkIfCanPlaceOrder(
    callback: (result: { canPlaceOrder: boolean; needSimulate: boolean }) => void,
    errorCallback?: (error: any) => void
  ): void {
    let params = new HttpParams().set('column', 'create_time').set('order', 'asc');

    this.http.get<RequestModel>(this.prefix + '/list', { params }).subscribe(
      (response: RequestModel) => {
        if (response.rtnData) {
          const { list, errorMessage } = response.rtnData;

          const needSimulate =
            list.some((product: any) => !product.isSimulated) &&
            list.every((product: any) =>
              product.errorMessages.every((error: any) => error.severity !== 'E')
            );

          const canPlaceOrder =
            list.length > 0 &&
            list.every((product: any) => product.isSimulated) &&
            list.every((product: any) =>
              product.errorMessages.every((error: any) => error.severity !== 'E')
            ) &&
            errorMessage.length === 0;

          callback({ canPlaceOrder, needSimulate });
        } else {
          callback({ canPlaceOrder: false, needSimulate: false });
        }
      },
      (error) => {
        console.error('Error fetching list:', error);
        if (errorCallback) {
          errorCallback(error);
        } else {
          callback({ canPlaceOrder: false, needSimulate: false });
        }
      }
    );
  }



  async updateAllProductsIsSimulatedStatus(isSimulated: boolean,form: FormGroup): Promise<void> {
    let params = {
      addressId: form.value.addressId,
      completeDelivery: form.value.completeDelivery,
      deliveryDate: this.commonService.formatDateString(form.value.deliveryDate),
      dispatchType: form.value.dispatchType,
      orderType: form.value.orderType || this.cartItemsSubject.getValue().selectedOrderType,
      poNumber: form.value.poNumber,
      brand:form.value.brand
    }
    const resetPromise = new Promise<void>((resolve, reject) => {
      this.http
        .post(this.prefix + `/resetSimulate`, params)
        .subscribe(
          () => {
            const currentItems = this.cartItemsSubject.value.list;
            //Modify the isSimulated status of all products in the shopping cart
            const updatedItems = currentItems.map((product) => ({
              ...product,
              isSimulated: isSimulated,
              simulateDeliveryInfoList: [],
              errorMessages: [],
              changeInfo: "",
              isGift: "",
              components: "",
            }));

            const cartData: CartData = {
              ...this.cartItemsSubject.value,
              list: updatedItems,
              simulateQuery:params
            };
            this.cartItemsSubject.next(cartData);
            resolve()
          },
          (error) => {
            console.error('Error updating:', error);
            reject(error);
          }
        )
    });
    await resetPromise;
  }
  updateShippingAddress(address: Address): void {
    const cartData: CartData = {
      ...this.cartItemsSubject.value,
      shippingAddress: address,
    };
    this.cartItemsSubject.next(cartData);
  }

  clearCart(): void {
    const emptyCartData: CartData = {
      list: [],
      shippingAddress: null,
      totalAmount: 0,
      netValue: 0,
      netPrice: 0,
      tax: 0,
      movSurcharge: 0,
      shipmentDimension: null,
      errorMessage: [],
      messages: [],
      shippingAddresses: [],
      simulateQuery:"",
      bonusDiscount:0,
      orderTypeList:[],
      totalFreight:0,
      selectedOrderType: undefined
    };
    this.cartItemsSubject.next(emptyCartData);
  }
  getInfoByResultId(params: any) {
    return this.http.get(this.prefix + '/asyncFail', { params });
  }
  /// get brand info
  getBrandList(params:any){
    const headers = new HttpHeaders().set('isUserCenterAdmin', 'true');
    return this.http.post<any>('/masterData/brand/page', params, { headers });
  }

}
